import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  InputBase,
  MenuItem,
  Select,
  styled,
  Switch,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { RadioProps, RadioGroupProps, ProgramModelSelectorProps, Option } from '../types';
import { buttonDisabledSx } from '../../../../../../../../components/ui/Buttons/buttonStyles';
import { useMemberWord } from '../../../../../../../../utils/memberTypeLabels';

const UiCheckbox = styled(Checkbox)(() => ({
  width: 22,
  height: 22,
  padding: 3,
  cursor: 'pointer',
  '& .MuiSvgIcon-root': { display: 'none' },
  '& .MuiTouchRipple-root': { display: 'none' },
  '&:not(.Mui-checked) .checkbox-box': {
    width: 16.5,
    height: 16.5,
    background: 'rgba(0,0,0,0.90)',
    borderRadius: 2,
    outline: '2px solid #868686',
    outlineOffset: '-1px'
  },
  '&.Mui-checked .checkbox-box': {
    width: 16.5,
    height: 16.5,
    background: 'transparent',
    borderRadius: 2,
    outline: '2px solid #FDFDFD',
    outlineOffset: '-1px',
    position: 'relative'
  },
  '&.Mui-checked .checkbox-box::after': {
    content: '""',
    position: 'absolute',
    width: 8,
    height: 4,
    borderLeft: '2px solid #FDFDFD',
    borderBottom: '2px solid #FDFDFD',
    transform: 'rotate(-45deg)',
    top: 4,
    left: 4
  },
  '&:hover .checkbox-box': { opacity: 0.85 }
}));

const UiSwitch = styled(Switch)(() => ({
  padding: 0,
  width: 40,
  height: 20,
  '& .MuiSwitch-track': {
    background: '#1C1C1C !important',
    border: '1px solid #565656 !important',
    borderRadius: 999,
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 2px'
  },
  '& .MuiSwitch-switchBase': {
    padding: 1,
    transition: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      '& .MuiSwitch-thumb': { background: '#F79420' },
      '& + .MuiSwitch-track': { borderColor: '#565656' }
    },
    '&.Mui-disabled': {
      '& .MuiSwitch-thumb': { background: '#868686' },
      '& + .MuiSwitch-track': { borderColor: '#565656' }
    }
  },
  '& .MuiSwitch-thumb': {
    width: 14,
    height: 14,
    borderRadius: 999,
    background: '#565656',
    transition: '300ms'
  }
}));

const UiInput = styled(InputBase)(() => ({
  padding: '10px 12px',
  borderRadius: '6px',
  background: 'rgba(0, 0, 0, 0.70)',
  outline: '1px solid #868686',
  color: '#FDFDFD',
  fontSize: 14,
  fontWeight: 400,
  transition: 'outline-color .15s ease',
  fontFamily: 'Inter, sans-serif',
  '&:hover': { outlineColor: '#B4B4B4' },
  '&.Mui-focused': { outlineColor: '#F79420' }
}));

const UiSelect = styled(Select)(() => ({
  color: '#fff',
  background: 'rgba(0, 0, 0, 0.70)',
  outline: '1px solid #868686',
  borderRadius: 1,
  '& .MuiSelect-icon': { color: '#fff' }
}));

export function CheckboxBox(props: any) {
  return (
    <UiCheckbox
      disableRipple
      icon={<div className="checkbox-box" />}
      checkedIcon={<div className="checkbox-box" />}
      {...props}
    />
  );
}

export function Radio({ checked, onChange, disabled = false }: RadioProps) {
  return (
    <Box
      onClick={() => !disabled && onChange()}
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.9)',
        border: checked ? '2px solid #FDFDFD' : '2px solid #868686',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: '150ms border-color',
        '&:hover': {
          borderColor: disabled ? (checked ? '#FDFDFD' : '#868686') : '#FDFDFD66'
        }
      }}
    >
      {checked && (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: '#FDFDFD'
          }}
        />
      )}
    </Box>
  );
}

export function RadioGroup({
  value,
  onChange,
  options,
  disabled = false
}: RadioGroupProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {options.map((opt) => (
        <Box
          key={opt.value}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
          onClick={() => !disabled && onChange(opt.value)}
        >
          <Radio
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
          />
          <Box sx={{ color: value === opt.value ? '#FDFDFD' : '#B6B6B6', fontSize: 14, lineHeight: '20px' }}>
            {opt.label}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function ProgramModelSelector({
  value,
  onChange,
  disabled = false
}: ProgramModelSelectorProps) {
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>Program type</Box>
      </Box>
      <Box sx={{ pt: 1, pb: 3 }}>
        <RadioGroup
          value={value}
          onChange={onChange}
          disabled={disabled}
          options={[
            { label: 'Course', value: 'masterclass' },
            { label: 'Guided Course', value: 'masterclass&mentorship' },
            { label: 'Mentorship', value: 'mentorship' }
          ]}
        />
      </Box>
    </Box>
  );
}

export function InputField({
  label,
  value,
  onChange,
  placeholder,
  ...props
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
} & Record<string, any>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
      {label && (
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif'
          }}
        >
          {label}
        </Typography>
      )}
      <UiInput
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        {...props}
      />
    </Box>
  );
}

export function CtaButton({ children, ...props }: any) {
  return (
    <Button
      {...props}
      disableRipple
      sx={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: '6px',
        background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
        color: '#FDFDFD',
        fontSize: '13px',
        fontWeight: 600,
        textTransform: 'none',
        fontFamily: 'Inter, sans-serif',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        '&:hover': {
          background: 'linear-gradient(180deg, #FFA135 0%, #F46A32 100%)'
        },
        '&:active': {
          background: 'linear-gradient(180deg, #D87C1C 0%, #CF4E1C 100%)'
        },
        '&.Mui-disabled': buttonDisabledSx
      }}
    >
      {children}
    </Button>
  );
}

export function SectionSelect({
  section,
  onChange
}: {
  section: string;
  onChange: (v: string) => void;
}) {
  return (
    <UiSelect
      value={section}
      onChange={(e) => onChange(String(e.target.value))}
      size="small"
      sx={{ width: '130px' }}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#000', color: '#fff' } } }}
    >
      <MenuItem value="settings">Settings</MenuItem>
      <MenuItem value="members">People</MenuItem>
    </UiSelect>
  );
}

export const SettingsRow = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `minmax(120px, 1fr) repeat(5, 72px)`,
      alignItems: 'center',
      alignContent: 'center',
      gap: 6,
      py: 1.2
    }}
  >
    {children}
  </Box>
);

// The role columns read as the org's own words (Settings > Members), not the program's labels.
export function RoleColumnsHeader() {
  const w = useMemberWord();
  return (
    <SettingsRow>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6">
        Required Onboarding Information
      </Typography>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6" textAlign="center">
        {w('mentor')}
      </Typography>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6" textAlign="center">
        {w('mentee')}
      </Typography>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6" textAlign="center">
        {w('admin')}
      </Typography>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6" textAlign="center">
        Mandatory
      </Typography>
      <Typography fontSize={12} fontWeight={700} color="#F79420" textAlign="center">
        Matching with Elliott
      </Typography>
    </SettingsRow>
  );
}

export function FieldRow({
  label,
  fieldKey,
  data = { mentee: false, mentor: false, admin: false, mandatory: false },
  onChange,
  onMandatoryChange,
  disabled = false
}: {
  label: string;
  fieldKey: string;
  data: { mentee: boolean; mentor: boolean; admin: boolean; mandatory: boolean };
  onChange: (role: 'mentor' | 'mentee' | 'admin', value: boolean) => void;
  onMandatoryChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  const safeData = {
    mentee: !!data?.mentee,
    mentor: !!data?.mentor,
    admin: !!data?.admin,
    mandatory: !!data?.mandatory
  };
  const handleChange = (role: 'mentor' | 'mentee' | 'admin', value: boolean) => {
    if (disabled) return;
    onChange(role, value);
  };
  const handleMandatoryChange = (value: boolean) => {
    if (disabled) return;
    onMandatoryChange(value);
  };
  return (
    <SettingsRow>
      <Typography fontSize={12} fontWeight={700} color="#B6B6B6">
        {label}
      </Typography>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <UiSwitch
          checked={safeData.mentor}
          disabled={disabled}
          onChange={(e) => handleChange('mentor', e.target.checked)}
        />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <UiSwitch
          checked={safeData.mentee}
          disabled={disabled}
          onChange={(e) => handleChange('mentee', e.target.checked)}
        />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <UiSwitch
          checked={safeData.admin}
          disabled={disabled}
          onChange={(e) => handleChange('admin', e.target.checked)}
        />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <CheckboxBox
          checked={safeData.mandatory}
          onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            handleMandatoryChange(checked)}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <AIFieldCheckbox fieldKey={fieldKey} />
      </Box>
    </SettingsRow>
  );
}

function AIFieldCheckbox({ fieldKey }: { fieldKey: string }) {
  const storageKey = 'dosen_ai_fields';
  const [checked, setChecked] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return !!stored[fieldKey];
    } catch { return false; }
  });

  return (
    <CheckboxBox
      checked={checked}
      onChange={(_e: React.ChangeEvent<HTMLInputElement>, val: boolean) => {
        setChecked(val);
        try {
          const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
          stored[fieldKey] = val;
          localStorage.setItem(storageKey, JSON.stringify(stored));
        } catch { /* ignore */ }
      }}
      sx={{
        '&.Mui-checked .checkbox-box': {
          outline: '1px solid #F79420 !important',
          background: '#F79420 !important'
        }
      }}
    />
  );
}

interface GenericAccordionProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  disabled?: boolean;
  rightContent?: React.ReactNode;
}

export function GenericAccordion({ title, children, defaultExpanded }: GenericAccordionProps) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{
        background: 'transparent',
        borderBottom: '1px solid #2A2A2A',
        color: '#FDFDFD',
        '&:before': { display: 'none' }
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ fontSize: 18, color: '#FDFDFD', transition: 'transform 0.2s' }} />
        }
        sx={{
          px: 0,
          py: 1.2,
          minHeight: 'unset',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-content': { margin: 0, display: 'flex', alignItems: 'center' }
        }}
      >
        {typeof title === 'string' ? (
          <Typography fontSize={12} fontWeight={700} color="white">
            {title}
          </Typography>
        ) : (
          title
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pt: 1 }}>{children}</AccordionDetails>
    </Accordion>
  );
}
